<template>
  <div class="new-signature">
    <page-header
      title="Assinatura"
      subtitle="Siga as etapas a seguir para cadastrar seu produto."
    />
    <page-container aside>
      <template #aside>
        <snow-steps v-model="currentStep" :steps="steps" vertical />
      </template>

      <template #default>
        <div v-show="currentStep === 2">
          <basic-information-add
            :form="form"
            :errors="errors"
            :loading="loading"
          />
        </div>
        <div v-show="currentStep === 3">
          <h2>Upload de uma imagem</h2>
          <div class="fs-14 text-neutral-400 mb-7">
            Essa imagem aparecerá para você na listagem de produtos e no
            checkout para seus compradores
          </div>
          <upload-image :form="form" />
        </div>
        <div v-if="currentStep === 4">
          <plan-information
            :form="form"
            :plans="plans"
            :errors="plan.errors"
            :loading="loading"
          />
        </div>
        <div v-if="currentStep === 5">
          <plan-new-signature
            :plans="plans"
            :paymentMethods="form.payment_methods_ids"
            :planToEdit="planToEdit"
            :showLoading="loading"
            @savePlan="savePlan"
            @submit="submitPlan"
            @edit="editPlan"
            @delete="removePlanList"
          />
        </div>
        <div
          class="d-flex flex-column flex-md-row justify-content-between my-9 px-3"
        >
          <bp-button
            color="neutral"
            class="order-1 order-md-0"
            variant="filled-1"
            @click="back"
            :icon="currentStep !== 1 ? 'fa-light fa-angle-left' : ''"
            :label="currentStep === 1 ? 'Cancelar' : 'Voltar'"
          />

          <bp-button
            :loading="loading"
            :disabled="!isValid"
            :color="isValid ? 'primary' : 'neutral'"
            :trailingIcon="
              currentStep !== steps.length ? 'fa-light fa-angle-right' : ''
            "
            :label="currentStep === steps.length ? 'Salvar Produto' : 'Próximo'"
            @click="next"
          />
        </div>
      </template>
    </page-container>
  </div>
</template>

<script>
import { formFields } from "@/functions/helpers.js";

import BasicInformationAdd from "@/components/product/BasicInformationAdd";
import PlanInformation from "@/components/product/PlanInformation.vue";
import UploadImage from "@/components/product/UploadImage";
import SnowSteps from "@/components/snow-ui/SnowSteps.vue";
import NewProductMixin from "@/mixins/newProduct";
import UnsavedChangesMixin from "@/mixins/unsavedChanges.js";
import PlanNewSignature from "../../components/product/PlanNewSignature";

export default {
  mixins: [NewProductMixin, UnsavedChangesMixin],
  components: {
    BasicInformationAdd,
    PlanInformation,
    UploadImage,
    PlanNewSignature,
    SnowSteps
  },
  mounted() {
    Promise.all([
      this.$store.dispatch("product/getCategories"),
      this.$store.dispatch("product/getPaymentMethods"),
      this.$store.dispatch("product/getCurrency")
    ])
      .then()
      .catch(error => {
        this.errors = error.response.data;
        if (this.errors.non_field_errors) {
          this.$message.error(this.errors.non_field_errors[0]);
        } else if (error.response.data.status === "notfound") {
          this.$message.error("Token inválido");
        }
      });
  },
  data() {
    return {
      currentStep: 2,
      loading: false,
      steps: [
        "Tipo de Produto",
        "Informações básicas",
        "Upload imagem",
        "Precificação",
        "Cadastrar plano"
      ],
      ...formFields(
        [
          "name",
          "pixel",
          "option_form",
          "currency",
          "category_id",
          "description",
          "charge_type",
          "type_product",
          "payment_methods_ids"
        ],
        {
          payment_methods_ids: [],
          charge_type: "recurrent",
          currency: "BRL",
          type_product: "signature",
          pixel: [],
          option_form: {
            request_address_shopper: false,
            request_cpf_cnpj_shopper: true,
            request_phone_shopper: false,
            coupon_shopper: false,
            instagram_shopper: false,
            request_zip_code: false,
            pos_address_shopper: false
          }
        }
      ),
      plan: formFields(
        [
          "name",
          "description",
          "price",
          "periodicity",
          "num_max_payments",
          "test_days",
          "product_id"
        ],
        {
          charge_type: "recurrent",
          type_product: "signature"
        }
      ),
      plans: [],
      planToEdit: null
    };
  },
  computed: {
    isValid() {
      const stepsValidation = {
        1: true,
        2: !!(
          this.form.name &&
          this.form.name.length <= 64 &&
          this.form.description &&
          this.form.description.length <= 980 &&
          this.form.category_id
        ),
        3: true,
        4: !!(
          this.form.currency &&
          this.form.payment_methods_ids &&
          this.form.warranty
        )
      };
      if (this.currentStep === 5) {
        return (
          this.plans.length &&
          (Object.values(stepsValidation).find(v => !v) ?? true)
        );
      }
      return stepsValidation[this.currentStep];
    }
  },
  watch: {
    currentStep(v) {
      if (v === 1) {
        this.$router.push({ name: "product-new" });
      }
    }
  },
  methods: {
    back() {
      if (this.currentStep === 1) {
        this.$router.go(-1);
      } else {
        this.currentStep--;
      }
    },
    next() {
      if (this.currentStep === this.steps.length) {
        this.submitPlan();
      } else {
        this.currentStep++;
      }
    },
    changeStep(step) {
      this.currentStep = step;
    },
    removePlanList(index) {
      this.plans.splice(index, 1);
    },
    createPlan() {
      this.planToEdit = null;
    },
    editPlan(index) {
      this.planToEdit = index;
    },
    savePlan(val) {
      if (this.planToEdit !== null) {
        const new_list = [...this.plans];
        new_list[this.planToEdit] = val;
        this.plans = new_list;
        this.planToEdit = null;
        return;
      }
      this.plans = [...this.plans, val];
    },
    async submitPlan() {
      let productSuccess = false;
      try {
        //submit product
        await this.submitProduct(
          { ...this.form, charge_type: "recurrent", type_product: "signature" },
          false,
          this.plans[0].price
        ); // internally commit in product/setProduct
        productSuccess = true;

        //create promisse all with all plans added
        await Promise.all(
          this.plans.map(plan =>
            this.$store.dispatch("plan/post", {
              product_id: this.product.id,
              status: "active",
              ...plan,
              price_eur: plan.price_eur === 0 ? "" : plan.price_eur,
              price_usd: plan.price_usd === 0 ? "" : plan.price_usd,
              test_days: plan.test_days || 0
            })
          )
        );
      } catch (error) {
        if (error.response && error.response.data.detail) {
          this.$message.error(error.response.data.detail);
        }
        if (error.response && error.response.data) {
          this.newProduct.errors = error.response ? error.response.data : {};
          let msg = [];
          for (let i in this.errors) {
            for (let j in this.errors[i]) {
              if (typeof this.errors[i][j] === "string") {
                msg.push(`${j}: ${this.errors[i][j]}`);
              } else {
                for (let k in this.errors[i][j]) {
                  msg.push(`${j}: ${this.errors[i][j][k]}`);
                }
              }
            }
          }
          if (msg.length) {
            this.$message.error(
              "Há erros no preenchimento do formulário, favor corrigir"
            );
          }
        }
      } finally {
        this.loading = false;
        if (productSuccess) {
          this.showSuccessModal = true;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-product-register {
  ::v-deep {
    .icon-angle-right,
    .icon-angle-left {
      font-size: 18px;
      margin-bottom: 1px;
      font-weight: 900;
    }
  }
}
</style>
